import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plane, Clock, CheckCircle2, ArrowRight, Info } from "lucide-react";

import Layout from "@/components/Layout";
import AddressInput from "@/components/booking/AddressInput";
import FleetSelector from "@/components/booking/FleetSelector";
import SquarePaymentForm from "@/components/booking/SquarePaymentForm";
import DurationGuardModal from "@/components/booking/DurationGuardModal";
import EstimatedPricingPanel from "@/components/booking/EstimatedPricingPanel";
import PopularLoganRoutesHint from "@/components/booking/PopularLoganRoutesHint";
import AccountBenefitsPanel from "@/components/booking/AccountBenefitsPanel";
import PricingDisclosure from "@/components/PricingDisclosure";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const SERVICE_TYPES = [
  { key: "point_to_point", label: "Point-to-Point" },
  { key: "hourly", label: "Hourly Charter" },
  { key: "airport", label: "Airport Transfer" },
];

const DRAFT_KEY = "phantasm_book_draft";

function buildQuoteInputs(form) {
  const inputs = {
    service_type: form.service_type,
    pickup_address: form.pickup_address.trim(),
    pickup_datetime: form.pickup_datetime,
  };
  if (form.service_type === "hourly") {
    inputs.duration_hours = Number(form.duration_hours) || 0;
  } else {
    inputs.dropoff_address = form.dropoff_address.trim();
  }
  if (form.service_type === "airport" && form.flight_number) {
    inputs.flight_number = form.flight_number.trim().toUpperCase();
  }
  return inputs;
}

export default function Book() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [form, setForm] = useState(() => {
    // Restore from sessionStorage if returning after login
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) return JSON.parse(draft);
    } catch {/* ignore */}
    return {
      service_type: "point_to_point",
      pickup_address: "",
      dropoff_address: "",
      pickup_datetime: "",
      duration_hours: 3,
      passengers: 1,
      luggage: 1,
      flight_number: "",
      special_requests: "",
    };
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  // Phase 16 P0-3 — guest checkout mode. Defaults to "guest" when not logged in.
  const [checkoutMode, setCheckoutMode] = useState("guest"); // "guest" | "auth"
  const [guestContact, setGuestContact] = useState({ name: "", email: "", phone: "" });
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [flightInfo, setFlightInfo] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const setField = (k) => (v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: typeof v === "string" || typeof v === "number" ? v : v.target.value };
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next)); } catch {/* */}
      return next;
    });
    setSelectedCategory(null);
    setSelectedQuote(null);
  };

  // Flight lookup (debounced)
  useEffect(() => {
    if (form.service_type !== "airport" || !form.flight_number || form.flight_number.length < 3) {
      setFlightInfo(null);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      const fn = form.flight_number.trim().toUpperCase();
      api
        .post("/quotes", {
          service_type: "airport",
          pickup_address: form.pickup_address || "JFK Airport",
          dropoff_address: form.dropoff_address || "Times Square",
          pickup_datetime: form.pickup_datetime || new Date().toISOString(),
          vehicle_category: "Luxury_Sedan",
          flight_number: fn,
        })
        .then(({ data }) => {
          if (alive) setFlightInfo(data.flight);
        })
        .catch(() => alive && setFlightInfo(null));
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [form.flight_number, form.service_type, form.pickup_address, form.dropoff_address, form.pickup_datetime]);

  const quoteInputs = useMemo(() => {
    try {
      return buildQuoteInputs(form);
    } catch {
      return null;
    }
  }, [form]);

  const onConfirmStart = () => {
    if (!user) {
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {/* */}
      navigate(`/login?next=/book`, { state: { from: "/book" } });
    }
  };

  const handleBookingSuccess = (booking) => {
    setConfirmed(booking);
    try { sessionStorage.removeItem(DRAFT_KEY); } catch {/* */}
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ------ Phase 12: Square checkout + 2-hour guardrail ------
  const [createdBooking, setCreatedBooking] = useState(null);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [durationModal, setDurationModal] = useState({ open: false, message: "" });

  // Allow the modal to be opened via URL ?guard=open — used for screenshot verification
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("guard") === "open") setDurationModal({ open: true, message: "" });
  }, []);

  // Prefill from ?pickup= / ?dropoff= query params (deep-links from /pricing route cards),
  // and scroll to the widget if ?focus=quote-widget is present.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const p = sp.get("pickup");
    const d = sp.get("dropoff");
    if (p) setField("pickup_address")(p);
    if (d) setField("dropoff_address")(d);
    if (sp.get("focus") === "quote-widget") {
      setTimeout(() => {
        document.querySelector('[data-testid="trip-inputs"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const durationMinutes = useMemo(() => {
    if (!selectedQuote) return 0;
    if (form.service_type === "hourly") return Number(form.duration_hours || 0) * 60;
    const miles = Number(selectedQuote.quote?.distance_miles || 0);
    return Math.round((miles / 35) * 60);
  }, [selectedQuote, form.service_type, form.duration_hours]);

  // Hourly charters have a 3-hour minimum which would otherwise always trip
  // the 2hr guardrail; hourly bookings are intentionally exempt and self-serve.
  const exceedsLimit = form.service_type !== "hourly" && durationMinutes > 120;

  const bookingPayload = useMemo(() => {
    if (!selectedCategory) return null;
    const payload = {
      ...quoteInputs,
      vehicle_category: selectedCategory,
      passengers: Number(form.passengers) || 1,
      luggage: Number(form.luggage) || 0,
      special_requests: form.special_requests || null,
      flight_number: form.flight_number ? form.flight_number.trim().toUpperCase() : null,
    };
    // Guest booking — attach guest_contact when the user isn't logged in.
    if (!user && checkoutMode === "guest") {
      payload.guest_contact = {
        name: guestContact.name,
        email: guestContact.email,
        phone: guestContact.phone,
      };
    }
    return payload;
  }, [quoteInputs, selectedCategory, form.passengers, form.luggage, form.special_requests, form.flight_number, user, checkoutMode, guestContact]);

  const guestContactValid =
    guestContact.name.trim().length >= 1 &&
    /^[^@]+@[^@]+\.[^@]+$/.test(guestContact.email) &&
    guestContact.phone.trim().length >= 6;

  if (confirmed) {
    return <ConfirmationView booking={confirmed} isGuest={!user} onReturnHome={() => navigate(user ? "/account" : "/")} onBookAgain={() => { setConfirmed(null); setSelectedCategory(null); setSelectedQuote(null); setCreatedBooking(null); }} />;
  }

  const showLoginGate = !authLoading && !user && selectedCategory;

  return (
    <Layout>
      <section
        data-testid="book-page"
        className="relative pt-32 pb-24 min-h-screen overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/85 via-[#0B0B0C]/90 to-[#0B0B0C]" aria-hidden />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-5">
              Reserve Your Chauffeur
            </div>
            <h1
              data-testid="book-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white"
            >
              Reserve your journey.
            </h1>
          </div>

          {/* Service Tabs */}
          <div
            data-testid="service-type-tabs"
            className="flex justify-center gap-2 sm:gap-0 mb-10 border-b border-white/10"
          >
            {SERVICE_TYPES.map((t) => {
              const active = form.service_type === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`service-tab-${t.key}`}
                  onClick={() => {
                    setForm((prev) => ({ ...prev, service_type: t.key }));
                    setSelectedCategory(null);
                    setSelectedQuote(null);
                  }}
                  className={`relative px-6 md:px-10 py-4 text-xs uppercase tracking-[0.25em] transition-colors ${
                    active ? "text-white" : "text-[#8E9AA6] hover:text-white"
                  }`}
                >
                  {t.label}
                  {active && (
                    <span className="absolute -bottom-px left-2 right-2 h-px bg-white" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Inside Phantasm — interior cabin atmosphere */}
          <figure
            data-testid="book-interior-showcase"
            className="mb-10 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-stretch"
          >
            <div className="relative overflow-hidden border border-white/10 backdrop-blur-xl bg-[#121214]/60 aspect-[16/9] md:aspect-auto">
              <img
                src="/assets/services/booking-interior-luxury.jpg"
                alt="Interior of a Phantasm vehicle — panoramic roof framing the Boston skyline at twilight."
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0B0B0C]/40"
                aria-hidden
              />
            </div>
            <figcaption className="flex flex-col justify-center backdrop-blur-xl bg-[#121214]/80 border border-white/10 px-6 md:px-8 py-6">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#C5A24A] mb-3">
                Inside Phantasm
              </div>
              <p className="font-serif italic text-[#E6E6E6] text-base md:text-lg leading-relaxed">
                Inside every Phantasm reservation — refined, panoramic, unmistakable.
              </p>
              <div className="mt-5 w-10 h-px bg-white/20" aria-hidden />
            </figcaption>
          </figure>

          {/* Popular Logan Routes hint — appears when pickup or dropoff contains "Logan" */}
          <PopularLoganRoutesHint
            pickupValue={form.pickup_address}
            dropoffValue={form.dropoff_address}
            onApply={({ pickup, dropoff }) => {
              setField("pickup_address")(pickup);
              if (dropoff && form.service_type !== "hourly") setField("dropoff_address")(dropoff);
            }}
          />

          {/* Main Glass Panel */}
          <div className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 shadow-2xl p-8 md:p-12 space-y-10">
            {/* Inputs */}
            <div data-testid="trip-inputs" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <AddressInput
                label="Pickup Address"
                value={form.pickup_address}
                onChange={setField("pickup_address")}
                placeholder="Where shall we collect you?"
                testid="pickup-address-input"
              />
              {form.service_type !== "hourly" && (
                <AddressInput
                  label="Drop-off Address"
                  value={form.dropoff_address}
                  onChange={setField("dropoff_address")}
                  placeholder="Where are you headed?"
                  testid="dropoff-address-input"
                  icon={form.service_type === "airport" ? "plane" : "pin"}
                />
              )}

              <div>
                <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                  Pickup Date &amp; Time
                </label>
                <input
                  type="datetime-local"
                  value={form.pickup_datetime}
                  onChange={(e) => setField("pickup_datetime")(e.target.value)}
                  data-testid="pickup-datetime-input"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                />
              </div>

              {form.service_type === "hourly" && (
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                    Duration (Hours)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    step={1}
                    value={form.duration_hours}
                    onChange={(e) => setField("duration_hours")(Number(e.target.value))}
                    data-testid="duration-hours-input"
                    className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                  />
                  {form.duration_hours > 0 && form.duration_hours < 3 && (
                    <div
                      data-testid="hourly-min-warning"
                      className="mt-2 text-[11px] text-amber-300 tracking-wide flex items-center gap-1.5"
                    >
                      <Info size={12} strokeWidth={1.5} /> 3-hour minimum applies — quote will reflect.
                    </div>
                  )}
                </div>
              )}

              {form.service_type === "airport" && (
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                    Flight Number
                  </label>
                  <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-white transition-colors">
                    <Plane size={16} strokeWidth={1.25} className="text-[#8E9AA6] shrink-0" />
                    <input
                      type="text"
                      value={form.flight_number}
                      onChange={(e) => setField("flight_number")(e.target.value.toUpperCase())}
                      data-testid="flight-number-input"
                      placeholder="e.g. DL412"
                      className="w-full bg-transparent px-0 py-3 text-white placeholder-[#8E9AA6] focus:outline-none font-sans text-base uppercase tracking-widest"
                    />
                  </div>
                  {flightInfo && flightInfo.valid && (
                    <div
                      data-testid="flight-info-chip"
                      className="mt-3 inline-flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.25em] bg-white/5 border border-white/15 px-3 py-2 text-white"
                    >
                      <span className="text-green-400">●</span>
                      {flightInfo.airline} · {flightInfo.status} · Gate {flightInfo.gate}
                    </div>
                  )}
                  {flightInfo && !flightInfo.valid && form.flight_number.length > 2 && (
                    <div
                      data-testid="flight-info-not-found"
                      className="mt-3 text-[11px] text-amber-300 tracking-wide"
                    >
                      Flight not found — confirm the code (e.g. AA1234, DL55).
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-6 md:col-span-2">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                    Passengers
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={56}
                    value={form.passengers}
                    onChange={(e) => setField("passengers")(Number(e.target.value))}
                    data-testid="passengers-input"
                    className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                    Luggage Pieces
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={56}
                    value={form.luggage}
                    onChange={(e) => setField("luggage")(Number(e.target.value))}
                    data-testid="luggage-input"
                    className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                  Special Requests
                </label>
                <textarea
                  rows={2}
                  value={form.special_requests}
                  onChange={(e) => setField("special_requests")(e.target.value)}
                  data-testid="special-requests-input"
                  placeholder="Child seat, signage, multiple stops…"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder-[#8E9AA6] focus:border-white focus:outline-none transition-colors font-sans text-base resize-none"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10" />

            {/* Fleet Selector */}
            <div>
              <div className="flex items-end justify-between mb-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">
                    Step Two
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-white font-semibold tracking-tight">
                    Select your vehicle
                  </h2>
                </div>
              </div>
              <FleetSelector
                quoteInputs={quoteInputs}
                selected={selectedCategory}
                onSelect={(cat, data) => {
                  setSelectedCategory(cat);
                  setSelectedQuote(data);
                }}
              />
            </div>

            {/* Quote summary + Checkout */}
            {selectedQuote && (
              <div data-testid="quote-checkout-panel" className="border-t border-white/10 pt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 animate-fade-in-up">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">
                    Step Three
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-white font-semibold tracking-tight mb-6">
                    Trip summary
                  </h2>
                  <QuoteBreakdown quote={selectedQuote.quote} />
                  <div className="mt-6">
                    <PricingDisclosure testid="book-pricing-disclosure" />
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">
                    Step Four
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-white font-semibold tracking-tight mb-6">
                    Checkout
                  </h2>
                  {!user ? (
                    <div data-testid="guest-or-signin" className="space-y-5">
                      <div className="grid grid-cols-2 gap-2 p-1 border border-white/10 bg-[#0B0B0C]/60">
                        <button
                          type="button"
                          data-testid="checkout-mode-guest"
                          onClick={() => setCheckoutMode("guest")}
                          className={`text-[11px] uppercase tracking-[0.25em] py-3 transition-colors ${
                            checkoutMode === "guest"
                              ? "bg-white text-black"
                              : "text-[#E6E6E6] hover:text-white"
                          }`}
                        >
                          Continue as Guest
                        </button>
                        <button
                          type="button"
                          data-testid="checkout-mode-signin"
                          onClick={() => {
                            setCheckoutMode("auth");
                            onConfirmStart();
                          }}
                          className={`text-[11px] uppercase tracking-[0.25em] py-3 transition-colors ${
                            checkoutMode === "auth"
                              ? "bg-white text-black"
                              : "text-[#E6E6E6] hover:text-white"
                          }`}
                        >
                          Sign In / Create Account
                        </button>
                      </div>

                      {checkoutMode === "guest" && exceedsLimit && !createdBooking && (
                        <div data-testid="duration-guard-inline-guest" className="space-y-4">
                          <p className="text-sm text-[#E6E6E6] font-light">
                            Trips exceeding 2 hours require a custom itinerary structure and a manual operator quote.
                          </p>
                          <button
                            type="button"
                            onClick={() => setDurationModal({ open: true, message: "" })}
                            data-testid="open-duration-guard-btn-guest"
                            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6C46E] px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
                          >
                            Request Custom Quote
                          </button>
                        </div>
                      )}

                      {checkoutMode === "guest" && !exceedsLimit && !createdBooking && (
                        <div data-testid="guest-contact-form" className="space-y-4">
                          <p className="text-[11px] uppercase tracking-[0.28em] text-[#8E9AA6]">
                            Guest Contact
                          </p>
                          <input
                            type="text"
                            placeholder="Full name"
                            value={guestContact.name}
                            data-testid="guest-name-input"
                            onChange={(e) => setGuestContact({ ...guestContact, name: e.target.value })}
                            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-white focus:outline-none font-sans text-base"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={guestContact.email}
                            data-testid="guest-email-input"
                            onChange={(e) => setGuestContact({ ...guestContact, email: e.target.value })}
                            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-white focus:outline-none font-sans text-base"
                          />
                          <input
                            type="tel"
                            placeholder="Phone"
                            value={guestContact.phone}
                            data-testid="guest-phone-input"
                            onChange={(e) => setGuestContact({ ...guestContact, phone: e.target.value })}
                            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-white focus:outline-none font-sans text-base"
                          />

                          <div className="mb-4">
                            <EstimatedPricingPanel
                              serviceType={form.service_type}
                              pickupAddress={form.pickup_address}
                              dropoffAddress={form.dropoff_address}
                              vehicleCategory={selectedCategory}
                              pickupDatetime={form.pickup_datetime}
                              durationHours={form.duration_hours}
                            />
                          </div>

                          <button
                            type="button"
                            data-testid="create-guest-booking-btn"
                            disabled={creatingBooking || !guestContactValid}
                            onClick={async () => {
                              if (!bookingPayload) return;
                              setSubmitError(""); setCreatingBooking(true);
                              try {
                                const { data } = await api.post("/bookings", bookingPayload);
                                setCreatedBooking(data);
                              } catch (e) {
                                setSubmitError(formatApiError(e));
                              } finally { setCreatingBooking(false); }
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
                          >
                            {creatingBooking ? "Reserving quote…" : (<>Continue to Payment <ArrowRight size={14} strokeWidth={1.5} /></>)}
                          </button>
                          {submitError && (
                            <div className="mt-3 text-xs text-red-400">{submitError}</div>
                          )}

                          {/* Optional account benefits panel */}
                          <AccountBenefitsPanel />
                        </div>
                      )}

                      {checkoutMode === "guest" && createdBooking && (
                        <SquarePaymentForm
                          bookingId={createdBooking.id}
                          totalPrice={createdBooking.total_price}
                          buyerEmail={createdBooking.guest_email}
                          manageToken={createdBooking.manage_token}
                          onSuccess={() => handleBookingSuccess(createdBooking)}
                          onDurationExceeded={({ message }) => setDurationModal({ open: true, message })}
                          onExpired={() => { setCreatedBooking(null); setSubmitError("Quote expired — please review and continue again."); }}
                        />
                      )}
                    </div>
                  ) : exceedsLimit ? (
                    <div data-testid="duration-guard-inline" className="space-y-4">
                      <p className="text-sm text-[#E6E6E6] font-light">
                        Trips exceeding 2 hours require a custom itinerary structure and a manual operator quote.
                      </p>
                      <button
                        type="button"
                        onClick={() => setDurationModal({ open: true, message: "" })}
                        data-testid="open-duration-guard-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6C46E] px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
                      >
                        Request Custom Quote
                      </button>
                    </div>
                  ) : !createdBooking ? (
                    <>
                      <div className="mb-5">
                        <EstimatedPricingPanel
                          serviceType={form.service_type}
                          pickupAddress={form.pickup_address}
                          dropoffAddress={form.dropoff_address}
                          vehicleCategory={selectedCategory}
                          pickupDatetime={form.pickup_datetime}
                          durationHours={form.duration_hours}
                        />
                      </div>
                      <button
                        type="button"
                        data-testid="create-booking-btn"
                        disabled={creatingBooking}
                        onClick={async () => {
                          if (!bookingPayload) return;
                          setSubmitError(""); setCreatingBooking(true);
                          try {
                            const { data } = await api.post("/bookings", bookingPayload);
                            setCreatedBooking(data);
                          } catch (e) {
                            setSubmitError(formatApiError(e));
                          } finally { setCreatingBooking(false); }
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
                      >
                        {creatingBooking ? "Reserving quote…" : (<>Continue to Payment <ArrowRight size={14} strokeWidth={1.5} /></>)}
                      </button>
                    </>
                  ) : (
                    <SquarePaymentForm
                      bookingId={createdBooking.id}
                      totalPrice={createdBooking.total_price}
                      buyerEmail={user?.email || createdBooking.guest_email}
                      manageToken={createdBooking.manage_token}
                      onSuccess={() => handleBookingSuccess(createdBooking)}
                      onDurationExceeded={({ message }) => setDurationModal({ open: true, message })}
                      onExpired={() => { setCreatedBooking(null); setSubmitError("Quote expired — please review and continue again."); }}
                    />
                  )}
                  {submitError && (
                    <div className="mt-4 text-xs text-red-400">{submitError}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <DurationGuardModal
        open={durationModal.open}
        onClose={() => setDurationModal({ open: false, message: "" })}
        message={durationModal.message}
      />
    </Layout>
  );
}

function QuoteBreakdown({ quote }) {
  if (!quote) return null;
  const items = quote.breakdown || [];
  return (
    <div data-testid="quote-breakdown" className="bg-[#0B0B0C]/50 border border-white/10 p-6 backdrop-blur-md">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">
        {quote.vehicle_category?.replace(/_/g, " ")} · {quote.service_type?.replace(/_/g, " ")}
      </div>
      <dl className="space-y-3">
        {items.map((line, idx) => {
          const isTotal = line.label === "Total";
          const isSubtotal = line.label === "Subtotal";
          return (
            <div
              key={`${line.label}-${idx}`}
              className={`flex items-baseline justify-between gap-4 ${
                isTotal ? "pt-4 mt-2 border-t border-white/15" : ""
              } ${isSubtotal ? "pt-3 mt-1 border-t border-white/5" : ""}`}
            >
              <div>
                <dt
                  className={`${
                    isTotal
                      ? "text-white text-sm uppercase tracking-[0.2em] font-medium"
                      : "text-[#E6E6E6] text-sm"
                  }`}
                >
                  {line.label}
                </dt>
                {line.note && (
                  <div className="text-[11px] text-[#8E9AA6] mt-0.5">{line.note}</div>
                )}
              </div>
              <dd
                className={`tabular-nums ${
                  isTotal
                    ? "font-serif text-2xl text-white font-semibold"
                    : "text-sm text-white"
                }`}
              >
                ${Number(line.amount).toFixed(2)}
              </dd>
            </div>
          );
        })}
      </dl>
      {quote.is_late_night && (
        <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5">
          <Clock size={12} strokeWidth={1.5} /> Late-night service window
        </div>
      )}
    </div>
  );
}

function ConfirmationView({ booking, isGuest, onReturnHome, onBookAgain }) {
  const manageUrl = isGuest && booking.manage_token
    ? `/booking/${booking.id}?token=${booking.manage_token}`
    : null;
  const email = booking.guest_email || booking.client_email || "your inbox";
  return (
    <Layout>
      <section
        data-testid="booking-confirmation"
        className="min-h-screen pt-32 pb-24 flex items-center justify-center relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#0B0B0C]/90 backdrop-blur-sm" aria-hidden />

        <div className="relative max-w-2xl mx-auto px-6 md:px-12 text-center">
          <CheckCircle2 size={56} strokeWidth={1.25} className="mx-auto text-white mb-8" />
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-5">
            Reservation Confirmed
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-semibold tracking-tighter">
            We&apos;ll see you soon.
          </h1>
          <p className="mt-5 text-sm text-[#E6E6E6] font-light max-w-md mx-auto">
            Your card has been authorized. A dispatcher will assign your chauffeur and send confirmation shortly.
          </p>
          <p data-testid="confirmation-email-note" className="mt-3 text-xs text-[#8E9AA6] max-w-md mx-auto">
            A confirmation and receipt have been sent to {email}.
          </p>

          <div className="mt-10 inline-block backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-8 py-6 text-left">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Booking ID</div>
            <div data-testid="confirmed-booking-id" className="font-mono text-sm text-white mt-1 break-all">
              {booking.id}
            </div>
            <div className="mt-5 text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Total Authorized</div>
            <div className="font-serif text-3xl text-white font-semibold mt-1">${booking.total_price.toFixed(2)}</div>
          </div>

          {manageUrl && (
            <div data-testid="guest-manage-link-panel" className="mt-8 max-w-lg mx-auto text-left backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-6 py-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">Manage Your Reservation</div>
              <p className="text-xs text-[#8E9AA6] leading-relaxed mb-3">
                Bookmark this link to review status, receipt, or cancel your trip.
              </p>
              <a
                href={manageUrl}
                data-testid="guest-manage-link"
                className="block text-sm text-white break-all underline hover:text-[#E6E6E6]"
              >
                {typeof window !== "undefined" ? `${window.location.origin}${manageUrl}` : manageUrl}
              </a>
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onReturnHome}
              data-testid="conf-go-account"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
            >
              {isGuest ? "Return Home" : "View My Bookings"}
            </button>
            <button
              onClick={onBookAgain}
              data-testid="conf-book-again"
              className="inline-flex items-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
            >
              Book Another
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
